import { matchPath } from "react-router-dom";
import { getFlatRoutes, PERMISSION_VIEW_MAP } from "@/config/routeConfig";
import {
  extractContextFromPath,
  getAllowedPortfolioCompanyIds,
  isAdminPath,
  isCommonPath,
  isFacilityPath,
  isPortfolioPath,
} from "@/utils/route-context";
import { normalizePermission } from "@/hooks/use-auth";
import { normalizeRole } from "@/hooks/use-role";

export const LINK_ACCESS_DENIED_MESSAGE = "You do not have access to this link";

const getPathAndSearch = (value = "") => {
  const [pathname = "", ...queryParts] = String(value).split("?");
  const query = queryParts.join("?");

  return {
    pathname: pathname || "",
    search: query ? `?${query}` : "",
  };
};

export const buildLoginRedirectPath = (locationLike = {}) => {
  const pathname = locationLike.pathname || "/";
  const search = locationLike.search || "";
  const redirectUrl = `${pathname}${search}`;
  const params = new URLSearchParams();

  params.set("redirectUrl", redirectUrl);

  return `/login?${params.toString()}`;
};

export const getSafeRedirectUrl = (search = "") => {
  const params = new URLSearchParams(search || "");
  const redirectUrl = params.get("redirectUrl");

  if (!redirectUrl) {
    return null;
  }

  if (!redirectUrl.startsWith("/") || redirectUrl.startsWith("//")) {
    return null;
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(redirectUrl)) {
    return null;
  }

  return redirectUrl;
};

const getViewTypeFromPath = (pathname = "") => {
  if (isFacilityPath(pathname)) return "facility";
  if (isPortfolioPath(pathname)) return "portfolio";
  if (isAdminPath(pathname)) return "admin";
  if (isCommonPath(pathname)) return "common";
  return null;
};

const findRouteForPath = (pathname = "", viewType) => {
  if (!viewType) {
    return null;
  }

  return getFlatRoutes(viewType).find((route) => matchPath({ path: route.path, end: true }, pathname)) || null;
};

const hasPermission = (permissions, code, isSuperUser) => {
  if (!code || isSuperUser) {
    return true;
  }

  return (permissions[code] || 0) > 0;
};

export const getRedirectAccessResult = (redirectUrl, authState = {}) => {
  const safeRedirectUrl = redirectUrl && redirectUrl.startsWith("/")
    ? getSafeRedirectUrl(`?redirectUrl=${encodeURIComponent(redirectUrl)}`)
    : null;

  if (!safeRedirectUrl) {
    return {
      allowed: false,
      redirectUrl: null,
      reason: "invalid",
    };
  }

  const { pathname } = getPathAndSearch(safeRedirectUrl);
  const viewType = getViewTypeFromPath(pathname);
  const route = findRouteForPath(pathname, viewType);

  if (!viewType || !route) {
    return {
      allowed: false,
      redirectUrl: safeRedirectUrl,
      reason: "unknown-route",
    };
  }

  const role = normalizeRole(authState.role);
  const isSuperUser = role === 1 || Boolean(authState.is_superuser);
  const permissions = normalizePermission(authState.permission);
  const context = extractContextFromPath(pathname);

  if (viewType === "facility") {
    const facilities = Array.isArray(authState.user_facilities) ? authState.user_facilities : [];
    const matchedFacility = facilities.find(
      (facility) =>
        String(facility.id) === String(context.facilityId) &&
        String(facility.company_id) === String(context.companyId)
    );

    if (!matchedFacility) {
      return {
        allowed: false,
        redirectUrl: safeRedirectUrl,
        reason: "facility-scope",
      };
    }

    if (!hasPermission(permissions, PERMISSION_VIEW_MAP.facility, isSuperUser)) {
      return {
        allowed: false,
        redirectUrl: safeRedirectUrl,
        reason: "facility-view",
      };
    }
  }

  if (viewType === "portfolio") {
    const allowedCompanyIds = getAllowedPortfolioCompanyIds(authState);
    const ownsCompanyContext = allowedCompanyIds.includes(String(context.companyId));

    if (!ownsCompanyContext) {
      return {
        allowed: false,
        redirectUrl: safeRedirectUrl,
        reason: "portfolio-scope",
      };
    }

    if (!hasPermission(permissions, PERMISSION_VIEW_MAP.portfolio, isSuperUser)) {
      return {
        allowed: false,
        redirectUrl: safeRedirectUrl,
        reason: "portfolio-view",
      };
    }
  }

  if (viewType === "admin" && !hasPermission(permissions, PERMISSION_VIEW_MAP.admin, isSuperUser)) {
    return {
      allowed: false,
      redirectUrl: safeRedirectUrl,
      reason: "admin-view",
    };
  }

  if (!hasPermission(permissions, route.code, isSuperUser)) {
    return {
      allowed: false,
      redirectUrl: safeRedirectUrl,
      reason: "route-permission",
    };
  }

  return {
    allowed: true,
    redirectUrl: safeRedirectUrl,
    context,
    route,
    viewType,
  };
};
